// ============================================
// CumulativeDeltaSubgraph.jsx
//
// LAYOUT (flex row inside .cvd-subgraph):
//   ┌──────────────────────────────┬─────────┐
//   │ .cvd-plot-area               │ .cvd-   │
//   │   <canvas plotCanvasRef />   │  y-axis │
//   │   candles + crosshair        │ <canvas │
//   │                              │  axis   │
//   │                              │  Ref/>  │
//   └──────────────────────────────┴─────────┘
//      width = plotWidth              60px
//
// The plot canvas is sized exactly to plotWidth, so candles whose x extends
// past plotWidth are naturally clipped by canvas bounds — that's the
// "slide under the y-axis" effect during scroll.
//
// CURSOR (mouseXRef sharing):
// The plot-area div captures pointermove and writes to mouseXRef directly.
// Both this subgraph's canvas AND the main canvas re-read mouseXRef every
// rAF tick, so the dashed crosshair tracks continuously across both panes.
// On pointerleave we reset to -1; if the cursor moved to the main canvas,
// its own handlers will re-set immediately (one-frame flicker, fine in
// practice).
//
// SCROLL SYNC (the lesson from earlier):
// Reads viewport state from the SAME refs the main canvas reads
// (viewEndIndexRef, viewWindowSizeRef, containerWidthRef, fractionalOffsetPxRef)
// inside its rAF — never derive viewport math from React state, or candles
// pop one bar at scroll boundaries. This rule applies to every chart layer.
// ============================================
import { useEffect, useRef, useState, memo } from 'react'
import {
  formatNumber,
  FIXED_OVERLAY_WIDTH,
} from '../utils/chartUtils'
import { CVD_DEFAULTS } from './CVDSettingsMenu'
import { requestFrame, cancelFrame, FRAME_LAYER } from '../utils/frameDriver'
import { subgraphX } from '../utils/subgraphX'
import { placeCrosshairs, registerCrosshairEl, CROSSHAIR_PANE_STYLE } from '../utils/crosshair'

const DEFAULT_HEIGHT = 100

const CVD_SCALE_KEY = 'solcheddar:cvdXScale'
// Discrete stops, not a continuous range: the useful values are few and a
// free slider invites 2.37×, which reads as a bug rather than a choice.
const CVD_SCALES = [1, 2, 3, 5, 8]

// ─── memo ───
// Rendered inside FootprintChart, which re-renders whenever pan state moves
// (setViewEndIndex fires once per candle-width of drag). Unmemoized, this
// re-rendered with it — and its draw effect's array deps then tore down and
// restarted the rAF loop mid-pan. Every prop it receives is a ref, a number,
// or a useMemo'd value (cvd.bars / cvd.startIndex come from
// useCumulativeDelta's useMemo), so during a pan none of them change and
// memo turns those re-renders into nothing.
//
// Requires updateCrosshair to be stable (useCallback in FootprintChartCanvas)
// — as a plain function it was a fresh identity every render and memo would
// have compared props, seen it differ, and re-rendered anyway.
function CumulativeDeltaSubgraph({
  bars,                    // Map<startTime, { open, high, low, close, delta }>
  candles,                 // FULL candles array
  axisWidth,          // live y-axis width from the chart (falls back to the constant)
  viewEndIndexRef,
  viewWindowSizeRef,
  containerWidthRef,
  paintNowRef,             // (resize-flash) chart's ref: set to a synchronous paint for its resize callback
  fractionalOffsetPxRef,
  frameRef,                // (subx) the chart's lastFrameRef — painted X geometry
  mouseXRef,
  scheduleMainCanvasDraw,  // callback that triggers a main-canvas redraw next frame
  onCursorXChange,         // (x: number) => void — DOM crosshair sync, -1 to hide
  startIndex,              // index of first live (delta != 0) candle; -1 if none yet
  height = DEFAULT_HEIGHT,
  config,                  // { style: 'candle'|'histogram', colors } — CVDSettingsMenu (cdhist-20261025)
}) {
  // ─── X-scale decoupling ───
  // CVD keeps the chart's viewEndIndex but widens its OWN window by this
  // factor, so the same pixel width holds S× more candles. Everything else
  // follows from that single change: candles get narrower, so a one-candle
  // pan of the main chart moves this pane's content by 1/S as many pixels —
  // the "scrolls slower" behaviour — and because both panes anchor the
  // newest candle at the same right edge, the front stays aligned and they
  // diverge going left. That is the entire point: more delta history in the
  // same strip, still married to the chart at the live edge.
  //
  // Sticky and GLOBAL (not per token): it is a way of reading cumulative
  // delta, not a property of any one instrument.
  const [xScale, setXScale] = useState(() => {
    try {
      const v = Number(localStorage.getItem(CVD_SCALE_KEY))
      return CVD_SCALES.includes(v) ? v : 1
    } catch { return 1 }
  })
  const [scaleUiHot, setScaleUiHot] = useState(false)
  const xScaleRef = useRef(xScale)
  useEffect(() => {
    xScaleRef.current = xScale
    try { localStorage.setItem(CVD_SCALE_KEY, String(xScale)) } catch {}
  }, [xScale])

  const plotCanvasRef = useRef(null)
  const axisCanvasRef = useRef(null)
  const plotAreaRef   = useRef(null)
  const yAxisRef      = useRef(null)
  const rafRef        = useRef(null)
  // Cached autoscale result — invalidated by viewport or live-close changes.
  // Avoids walking N candles every frame when nothing relevant moved.
  const autoscaleCacheRef = useRef(null)
  // Latest lo/hi for the visible window. Written once per draw frame, read
  // by the y-axis pointer handler to map cursor Y → CVD value.
  const yScaleRef = useRef({ lo: 0, hi: 0 })

  // Y-axis magnifier — mirrors the main chart's y-axis tooltip behavior.
  // State lives in the subgraph since the value math is CVD-specific.
  // Imperative (ref + DOM writes, like the chart's own cursor label): a React
  // setState per pointer move re-rendered the whole pane every move.
  const yMagRef = useRef(null)
  const showYMag = (y, value) => {
    const el = yMagRef.current
    if (!el) return
    el.style.top = `${y}px`
    el.textContent = formatTag(value)
    el.style.display = ''
  }
  const hideYMag = () => { if (yMagRef.current) yMagRef.current.style.display = 'none' }
  // Vertical crosshair: a DOM line moved in the same call as the chart's
  // (utils/crosshair placeCrosshairs), not painted in the rAF loop.
  const xhairRef = useRef(null)
  useEffect(() => registerCrosshairEl(mouseXRef, xhairRef.current), [mouseXRef])
  const onAxisPointerMove = (e) => {
    const rect = yAxisRef.current?.getBoundingClientRect()
    if (!rect) return
    const y    = e.clientY - rect.top
    const { lo, hi } = yScaleRef.current
    const range = hi - lo
    if (!range) return
    // Same yFor inversion: y = H - ((v - lo) / range) * H  →  v = hi - (y/H)*range
    const value = hi - (y / rect.height) * range
    showYMag(y, value)
  }
  const onAxisPointerLeave = () => {
    hideYMag()
  }

  // Hold latest props in refs so the rAF loop always has fresh data without
  // re-creating the loop on every prop change.
  const barsRef       = useRef(bars)
  const candlesRef    = useRef(candles)
  const startIndexRef = useRef(startIndex)
  barsRef.current       = bars
  candlesRef.current    = candles
  startIndexRef.current = startIndex
  // Style/colors read by the rAF loop through a ref, so a menu change
  // repaints on the next frame without restarting the loop.
  const configRef = useRef(config)
  configRef.current = { ...CVD_DEFAULTS, ...(config || {}) }

  // ===== Shared cursor handlers =====
  // mouseXRef: shared with main canvas for crosshair sync.
  // yMagnifier: tracks cursor Y while over the plot area, showing the
  // CVD value at that pixel position. Mirrors the main chart's price-
  // label that follows the cursor anywhere over the chart body.
  const onPlotPointerMove = (e) => {
    const rect = plotAreaRef.current?.getBoundingClientRect()
    if (!rect) return
    const x = e.clientX - rect.left
    mouseXRef.current = x
    // Sync the main chart's DOM crosshair to follow our cursor.
    // Replaces the previous scheduleMainCanvasDraw() which triggered a
    // full canvas redraw — now an imperative DOM transform update.
    if (onCursorXChange) onCursorXChange(x)
    else placeCrosshairs(mouseXRef, x)

    // Cursor follower: same math as the y-axis magnifier handler.
    const y = e.clientY - rect.top
    const { lo, hi } = yScaleRef.current
    const range = hi - lo
    if (range) {
      const value = hi - (y / rect.height) * range
      showYMag(y, value)
    }
  }
  const onPlotPointerLeave = () => {
    mouseXRef.current = -1
    if (onCursorXChange) onCursorXChange(-1)
    else placeCrosshairs(mouseXRef, -1)
    hideYMag()
  }

  // X-wheel ownership stays on FootprintChartCanvas. Its native body listener
  // captures the cursor anchor and drives the shared Animator; handling wheel
  // here too would apply a second, unanchored state update before that anchor
  // is solved and makes this pane jump against the footprint.

  useEffect(() => {
    let cancelled = false

    const draw = () => {
      if (cancelled) return
      const plotCanvas = plotCanvasRef.current
      const axisCanvas = axisCanvasRef.current
      if (!plotCanvas || !axisCanvas) {
        rafRef.current = requestFrame(draw, FRAME_LAYER); return
      }

      // Read EVERYTHING from refs — no state-derived inputs
      const containerW = containerWidthRef.current
      const S          = xScaleRef.current || 1
      // (subx) X comes from the chart's PAINTED frame (lastFrameRef), read at
      // FRAME_LAYER after the chart's FRAME_DRAW, with its X camera applied —
      // not re-derived from the slot refs. See utils/subgraphX.js.
      const frame      = frameRef ? frameRef.current : null
      const mouseX     = mouseXRef.current
      const allCandles = (frame && frame.candles) || candlesRef.current
      const allBars    = barsRef.current
      const total      = allCandles?.length || 0

      const dpr       = window.devicePixelRatio || 1
      const H         = height
      const plotWidth = Math.max(50, containerW - FIXED_OVERLAY_WIDTH)
      // The chart's y-axis WIDTH IS DYNAMIC — it grows with market-cap digit
      // count (yAxisWidth in FootprintChartCanvas). Hardcoding the 60px
      // constant left the subgraph gutters misaligned with the price ladder
      // on big-cap tokens. Take the live value when the chart supplies it.
      const axisW     = axisWidth || FIXED_OVERLAY_WIDTH

      // ----- Size canvases (CSS + backing store) -----
      const setCanvasSize = (canvas, cssW, cssH) => {
        const needW = cssW * dpr, needH = cssH * dpr
        if (canvas.width !== needW || canvas.height !== needH) {
          canvas.width  = needW
          canvas.height = needH
        }
        canvas.style.width  = `${cssW}px`
        canvas.style.height = `${cssH}px`
      }
      setCanvasSize(plotCanvas, plotWidth, H)
      setCanvasSize(axisCanvas, axisW,     H)

      const pctx = plotCanvas.getContext('2d')
      const actx = axisCanvas.getContext('2d')
      pctx.setTransform(dpr, 0, 0, dpr, 0, 0)
      actx.setTransform(dpr, 0, 0, dpr, 0, 0)
      pctx.clearRect(0, 0, plotWidth, H)
      actx.clearRect(0, 0, axisW,     H)

      // Background panel — only the plot area gets the subgraph bg tint.
      // Y-axis canvas stays transparent so it visually merges with the
      // surrounding chart area; otherwise its solid fill shows as a
      // narrow strip at the right edge wherever the canvas height extends
      // past the actual data, creating a visible vertical artifact.
      pctx.fillStyle = 'rgba(15, 17, 22, 0.35)'
      pctx.fillRect(0, 0, plotWidth, H)

      // ----- Empty / no-data fallback -----
      if (total === 0 || !allBars || allBars.size === 0) {
        drawZeroLine(pctx, plotWidth, H / 2)
        rafRef.current = requestFrame(draw, FRAME_LAYER)
        return
      }

      // ----- Viewport: the chart's painted columns (subx) -----
      const g = subgraphX(frame, S)
      if (!g) {
        drawZeroLine(pctx, plotWidth, H / 2)
        rafRef.current = requestFrame(draw, FRAME_LAYER)
        return
      }
      const startIdx = g.start
      const endIdx   = Math.min(g.end, total - 1)
      const pxPerCandle = g.cw
      const X = g.xLeft

      // ----- Y autoscale across the VISIBLE window only -----
      // Wicks are still in the scale — but only for visible candles. As
      // you scroll past a tall-wick bar, that bar's high/low leave the
      // scan and the scale relaxes. Without this restriction the session's
      // most extreme bar pins the scale forever and the rest of the
      // chart drags in empty bands above/below the data.
      //
      // Anchor exclusions still apply: the anchor bar's open is 0 by
      // definition and its low can also be 0 if the bar wasn't observed
      // building, so both are skipped for that one bar.
      //
      // Cache invalidates on window/data change. Loop is small (~visible
      // candle count) so cache hits are a minor win, not load-bearing.
      const cache = autoscaleCacheRef.current
      const liveCandle = allCandles[total - 1]
      const liveOhlc   = liveCandle ? allBars.get(liveCandle.startTime) : null
      const liveCloseSig = liveOhlc ? liveOhlc.close : null
      const startIdxAnchorForScale = startIndexRef.current >= 0 ? startIndexRef.current : 0
      const scanFrom = Math.max(startIdxAnchorForScale, startIdx)
      const scanTo   = Math.min(endIdx, total - 1)
      const cfg = configRef.current
      const isHist = cfg.style === 'histogram'
      // Histogram scales on per-candle delta (bars from zero, so zero is
      // always in range); the live bar's delta is part of the key.
      const cacheKey = `${isHist ? 'h' : 'c'}|${scanFrom}|${scanTo}|${liveCloseSig}|${liveOhlc ? liveOhlc.delta : ''}`
      let minV, maxV
      if (cache && cache.key === cacheKey) {
        minV = cache.minV
        maxV = cache.maxV
      } else if (isHist) {
        minV = 0; maxV = 0
        for (let i = scanFrom; i <= scanTo; i++) {
          const c = allCandles[i]
          if (!c) continue
          const ohlc = allBars.get(c.startTime)
          if (!ohlc) continue
          const d = ohlc.delta || 0
          if (d > maxV) maxV = d
          if (d < minV) minV = d
        }
        autoscaleCacheRef.current = { key: cacheKey, minV, maxV }
      } else {
        minV = Infinity; maxV = -Infinity
        for (let i = scanFrom; i <= scanTo; i++) {
          const c = allCandles[i]
          if (!c) continue
          const ohlc = allBars.get(c.startTime)
          if (!ohlc) continue
          if (i === startIdxAnchorForScale) {
            // Anchor bar: open is 0 by definition (CVD always starts at
            // zero at the anchor). If the bar wasn't observed building,
            // its low is also 0. Both are session-start artifacts that
            // shouldn't pin the scale. Use close + high only.
            if (ohlc.close > maxV) maxV = ohlc.close
            if (ohlc.close < minV) minV = ohlc.close
            if (ohlc.high  > maxV) maxV = ohlc.high
          } else {
            if (ohlc.open  > maxV) maxV = ohlc.open
            if (ohlc.open  < minV) minV = ohlc.open
            if (ohlc.close > maxV) maxV = ohlc.close
            if (ohlc.close < minV) minV = ohlc.close
            // Wicks in scale — full bar (body + wicks) visible.
            if (ohlc.high  > maxV) maxV = ohlc.high
            if (ohlc.low   < minV) minV = ohlc.low
          }
        }
        autoscaleCacheRef.current = { key: cacheKey, minV, maxV }
      }

      if (!Number.isFinite(minV) || !Number.isFinite(maxV)) {
        drawZeroLine(pctx, plotWidth, H / 2)
        rafRef.current = requestFrame(draw, FRAME_LAYER)
        return
      }
      // Autoscale tight to the actual visible data range. Zero is NOT
      // forced into view — when CVD is far from zero, including zero
      // crushes the bars to one edge of the canvas. Zero line still
      // renders if it falls inside the natural data range; otherwise it's
      // offscreen and the bars use the full vertical space.
      //
      // Pixel-based gutter: reserve GUTTER_PX at the top and bottom of
      // the panel regardless of dataSpan, so visual breathing room is
      // consistent across tokens and timeframes. A percentage of dataSpan
      // (the old approach) shrinks to nothing when the range is small
      // and lets the scale floor stay near 0 when minV is small.
      const GUTTER_PX = 24   // headroom so the extreme ticks aren't jammed on the edges
      const dataSpan = (maxV - minV) || 1
      const padFraction = GUTTER_PX / Math.max(1, H - 2 * GUTTER_PX)
      const pad   = dataSpan * padFraction
      const lo    = minV - pad
      const hi    = maxV + pad
      const range = hi - lo || 1
      // Stash for the y-axis magnifier handler — it needs the same scale
      // the renderer just used to invert cursor-y back to a CVD value.
      yScaleRef.current = { lo, hi }
      const yFor  = v => H - ((v - lo) / range) * H
      const yZero = yFor(0)

      // Find latest visible CVD close for the current-value tag
      let latestClose = null
      for (let i = endIdx; i >= startIdx; i--) {
        const c = allCandles[i]
        if (!c) continue
        const ohlc = allBars.get(c.startTime)
        // Histogram: the tag/helper line track the latest bar's delta.
        if (ohlc) { latestClose = isHist ? (ohlc.delta || 0) : ohlc.close; break }
      }

      // ===== PLOT CANVAS =====
      // Zero line — only when zero is within the visible range
      if (yZero >= 0 && yZero <= H) {
        drawZeroLine(pctx, plotWidth, yZero)
      }

      // Start marker — vertical dashed line at the first live (delta != 0)
      // candle. Reads as "CVD started here." Only drawn if the anchor is
      // within the visible window; otherwise it's offscreen anyway.
      const startIdxAnchor = startIndexRef.current
      if (startIdxAnchor >= startIdx && startIdxAnchor <= endIdx) {
        const startX = X(startIdxAnchor)
        if (startX >= 0 && startX <= plotWidth) {
          pctx.save()
          pctx.strokeStyle = 'rgba(255, 200, 80, 0.55)'
          pctx.lineWidth   = 1
          pctx.setLineDash([3, 3])
          pctx.beginPath()
          pctx.moveTo(startX, 0)
          pctx.lineTo(startX, H)
          pctx.stroke()
          pctx.restore()
        }
      }

      // Candles — same positioning math as drawCanvas
      const cw       = pxPerCandle
      const bodyW    = Math.max(1, cw * 0.78)
      const bodyOff  = (cw - bodyW) / 2
      const greenFill = cfg.upColor
      const greenLine = cfg.wickColor
      const redFill   = cfg.downColor
      const redLine   = cfg.wickColor
      const dojiFill  = 'rgba(160, 170, 190, 0.7)'

      for (let candleIdx = startIdx; candleIdx <= endIdx; candleIdx++) {
        // Nothing to say before the first live candle: historical bars are
        // seeded from OHLCV (no buy/sell split), so their delta is 0 by
        // construction and the cumulative line is a flat zero tail — pure
        // noise. CVD begins AT the dashed start marker, Quantower-style
        // (their period anchor; ours is the live observation window).
        if (startIdxAnchor >= 0 && candleIdx < startIdxAnchor) continue
        const candle = allCandles[candleIdx]
        if (!candle) continue
        const ohlc = allBars.get(candle.startTime)
        if (!ohlc) continue

        const x = X(candleIdx)

        // Cull fully-offscreen candles. Partially-clipped ones are drawn
        // and naturally cut by canvas bounds — that's the slide-under.
        if (x + cw < 0)         continue
        if (x       > plotWidth) continue

        if (isHist) {
          // Histogram: this candle's own delta as a bar from zero.
          const d = ohlc.delta || 0
          if (!d) continue
          const yD   = yFor(d)
          const top  = Math.min(yZero, yD)
          const barH = Math.max(1, Math.abs(yD - yZero))
          pctx.fillStyle = d > 0 ? cfg.histPos : cfg.histNeg
          pctx.fillRect(x + bodyOff, top, bodyW, barH)
          continue
        }

        const isUp   = ohlc.close > ohlc.open
        const isDn   = ohlc.close < ohlc.open
        const fill   = isUp ? greenFill : isDn ? redFill : dojiFill
        const stroke = isUp ? greenLine : isDn ? redLine : dojiFill

        pctx.strokeStyle = stroke
        pctx.lineWidth   = 1
        pctx.beginPath()
        pctx.moveTo(x + cw / 2, yFor(ohlc.high))
        pctx.lineTo(x + cw / 2, yFor(ohlc.low))
        pctx.stroke()

        const yOpen  = yFor(ohlc.open)
        const yClose = yFor(ohlc.close)
        const bodyTop = Math.min(yOpen, yClose)
        const bodyBot = Math.max(yOpen, yClose)
        const bodyH   = Math.max(1, bodyBot - bodyTop)
        pctx.fillStyle = fill
        pctx.fillRect(x + bodyOff, bodyTop, bodyW, bodyH)
      }

      // Vertical crosshair is a DOM line now (xhairRef, placed by
      // utils/crosshair placeCrosshairs in the pointer handler) — not painted
      // here, where it trailed the chart's line by a frame or more.

      // Current-value horizontal helper — dashed line at latest CVD close,
      // extending across the plot canvas to the right edge. Mirrors the
      // main chart's price marker line so the eye tracks the live level.
      if (latestClose !== null) {
        const yCur = yFor(latestClose)
        if (yCur >= 0 && yCur <= H) {
          pctx.save()
          pctx.strokeStyle = 'rgba(120, 130, 150, 0.55)'
          pctx.lineWidth   = 1
          pctx.setLineDash([4, 4])
          pctx.beginPath()
          pctx.moveTo(0, yCur)
          pctx.lineTo(plotWidth, yCur)
          pctx.stroke()
          pctx.restore()
        }
      }

      // ===== Y-AXIS CANVAS =====
      // Zero line continues across the axis band — only when in range
      if (yZero >= 0 && yZero <= H) {
        actx.strokeStyle = 'rgba(160, 170, 190, 0.35)'
        actx.lineWidth   = 1
        actx.setLineDash([4, 3])
        actx.beginPath(); actx.moveTo(0, yZero); actx.lineTo(axisW, yZero); actx.stroke()
        actx.setLineDash([])
      }

      // Tick labels: 6 evenly-spaced ticks across the visible range.
      // Includes hi (top), lo (bottom), zero gets a dimmer label if in range.
      actx.font         = '400 14px "Nova Square", system-ui, sans-serif'   // = the main chart's .y-axis-row
      actx.textBaseline = 'middle'
      actx.textAlign    = 'center'   // = .y-axis-row's justify-content: center
      // Tick COUNT and the hi..lo span stay CVD's own: cumulative delta
      // wanders and its range is asymmetric, so ticks are spread across the
      // actual visible extremes rather than DVP's symmetric ±max. Only the
      // zero handling is borrowed.
      const N_TICKS = 6
      for (let t = 0; t < N_TICKS; t++) {
        const frac = t / (N_TICKS - 1)
        const v    = hi - frac * range
        // Skip anything NEAR zero, not just exact zero. The old guard lived
        // in formatTag (`v === 0`) and almost never fired, because a tick
        // landing on the zero line by exact equality is a coincidence — so
        // the label the comment claims was removed still printed, sitting on
        // top of the dashed baseline that already marks it. Proximity is the
        // test DVP uses and it is the one that actually works.
        if (range > 0 && Math.abs(v) < range * 0.04) continue
        const y    = frac * H
        const yClamped = Math.max(8, Math.min(H - 8, y))
        actx.fillStyle = '#646464'   // = .y-axis-row color
        actx.fillText(formatTag(v), axisW / 2, yClamped)
      }

      // Current-value tag (colored)
      if (latestClose !== null) {
        const yCur = yFor(latestClose)
        const text = formatTag(latestClose)
        // FIXED width, text CENTERED: the chip used to be sized to the text
        // (measureText + 12), so every digit change resized it and the number
        // appeared to wander horizontally. Now only its Y moves.
        const tagW = axisW            // full gutter span, like the price chip
        const tagX = 0
        const tagY = Math.max(8, Math.min(H - 8, yCur))
        // Same pair as DeltaVolumePro's chip. The gutters stack directly on
        // top of each other, so two different greens read as two components
        // each with an opinion rather than one instrument. Values, ranges and
        // tick placement stay CVD's own — only the palette is shared.
        actx.fillStyle = latestClose >= 0
          ? 'rgba(38,166,154,0.92)'
          : 'rgba(239,83,80,0.92)'
        actx.fillRect(tagX, tagY - 8, tagW, 16)
        actx.fillStyle = '#0a0a0a'
        actx.font      = '600 14px "Nova Square", system-ui, sans-serif'
        actx.textBaseline = 'middle'
        actx.textAlign = 'center'
        actx.fillText(text, tagX + tagW / 2, tagY)
      }

      rafRef.current = requestFrame(draw, FRAME_LAYER)
    }

    rafRef.current = requestFrame(draw, FRAME_LAYER)
    // (resize-flash) The chart's resize callback paints this pane in the
    // same frame as itself: drop the queued frame, draw now (every exit of
    // draw re-arms exactly one frame).
    if (paintNowRef) paintNowRef.current = () => { if (cancelled) return; if (rafRef.current) cancelFrame(rafRef.current); draw() }
    return () => {
      cancelled = true; if (rafRef.current) cancelFrame(rafRef.current)
      if (paintNowRef) paintNowRef.current = null
    }
  }, [
    viewEndIndexRef,
    viewWindowSizeRef,
    containerWidthRef,
    fractionalOffsetPxRef,
    mouseXRef,
    height,
  ])

  return (
    <div
      className="cvd-subgraph"
      style={{ height: `${height}px`, display: 'flex', flexDirection: 'row', position: 'relative' }}
    >
      <div
        ref={plotAreaRef}
        className="cvd-plot-area"
        style={{ flex: 1, position: 'relative', overflow: 'hidden', cursor: 'crosshair', borderTop: '1px solid #252525', }}
        onPointerMove={onPlotPointerMove}
        onPointerLeave={onPlotPointerLeave}
      >
        <canvas ref={plotCanvasRef} style={{ display: 'block' }} />
        {/* Vertical crosshair (DOM, same-frame as the chart's; the plot area's
            overflow:hidden clips it off the y-axis). */}
        <div ref={xhairRef} className="chart-crosshair" style={CROSSHAIR_PANE_STYLE} />

        {/* X-scale control — bottom centre, over the plot. Discrete stops so
            the value is always a number you'd choose on purpose. Dimmed until
            hover: it sits on the data, and a permanent control in the middle
            of a reading surface is worse than one you have to reach for.
            pointerEvents stay on so it's reachable without a hover first
            (touch has no hover at all). */}
        <div
          className="cvd-xscale"
          style={{
            position: 'absolute',
            left: '50%',
            bottom: 3,
            transform: 'translateX(-50%)',
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            padding: '2px 4px',
            borderRadius: 4,
            background: 'rgba(12,14,18,0.72)',
            border: '1px solid #23262d',
            opacity: scaleUiHot ? 1 : 0.28,
            transition: 'opacity 0.15s',
            zIndex: 3,
          }}
          // MOUSE enter/leave, not POINTER. The plot area's drag handler takes
          // setPointerCapture on pointerdown, which retargets every later
          // pointer event to the capturing element — so onPointerLeave fired
          // the instant you pressed a button, the control faded, and the click
          // never landed. Mouse events are not retargeted by pointer capture,
          // so they survive the press.
          onMouseEnter={() => setScaleUiHot(true)}
          onMouseLeave={() => setScaleUiHot(false)}
          // And stop the press reaching the plot area at all: without this the
          // chart starts a drag under the control, so clicking a stop would
          // also pan the chart.
          onPointerDown={(e) => { e.stopPropagation(); setScaleUiHot(true) }}
          onPointerMove={(e) => e.stopPropagation()}
        >
          {CVD_SCALES.map(v => (
            <button
              key={v}
              onPointerDown={(e) => e.stopPropagation()}
              onClick={(e) => { e.stopPropagation(); setXScale(v) }}
              title={v === 1 ? 'Match the chart' : `${v}× more history in the same width`}
              style={{
                background: v === xScale ? '#26a69a22' : 'transparent',
                border: '1px solid ' + (v === xScale ? '#26a69a66' : 'transparent'),
                color: v === xScale ? '#26a69a' : '#6b7280',
                font: '600 10px "Nova Square", system-ui, sans-serif',
                padding: '1px 5px',
                borderRadius: 3,
                cursor: 'pointer',
                lineHeight: 1.4,
              }}
            >
              {v}×
            </button>
          ))}
        </div>
      </div>
      <div
        ref={yAxisRef}
        className="cvd-y-axis"
        style={{ width: `${FIXED_OVERLAY_WIDTH}px`, position: 'relative', flexShrink: 0, borderTop: '1px solid #252525' }}
        onPointerMove={onAxisPointerMove}
        onPointerLeave={onAxisPointerLeave}
      >
        <canvas ref={axisCanvasRef} style={{ display: 'block' }} />
        <div
          ref={yMagRef}
          className="price-label"
          style={{
            display: 'none',
            left: 0,
            right: 0,
            transform: 'translateY(-50%)',
          }}
        />
      </div>
    </div>
  )
}

function drawZeroLine(ctx, plotWidth, yZero) {
  ctx.save()
  ctx.strokeStyle = 'rgba(160, 170, 190, 0.35)'
  ctx.lineWidth   = 1
  ctx.setLineDash([4, 3])
  ctx.beginPath()
  ctx.moveTo(0, yZero)
  ctx.lineTo(plotWidth, yZero)
  ctx.stroke()
  ctx.restore()
}

function formatTag(v) {
  if (!v) return '0'
  // Whole numbers only on the axis — decimals on a delta scale are noise
  // (formatNumber's 2dp made every tick read like a price).
  //
  // SIGN: the negative branch used to be an EMPTY string while `a` had
  // already taken the magnitude, so -500 rendered as "500" — every tick and
  // magnifier readout below zero claimed to be positive. On a cumulative
  // delta axis that is half the range misreporting its direction, and the
  // axis is the only thing saying which side of zero the line is on.
  // DeltaVolumePro's fmtDelta has always had this right; kept identical here
  // so the two gutters read the same.
  const a = Math.abs(v)
  const t = a >= 1e9 ? Math.round(a / 1e9) + 'B'
          : a >= 1e6 ? Math.round(a / 1e6) + 'M'
          : a >= 1e3 ? Math.round(a / 1e3) + 'K'
          : String(Math.round(a))
  return (v > 0 ? '+' : '-') + t
}

export default memo(CumulativeDeltaSubgraph)